import { SegmentOption } from "components/designV2/segmentcontroller/segmentcontroller";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Api } from "services/api/api.service";
import { useAlertStore } from "stores/alertStore/alertStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import {
  CATEGORY_SETTING_KEYS,
  DEFAULT_FORM,
  buildDiffPlan,
  buildInitialFormData,
  getActiveCategoryHasSettings,
  getCategoryOptions,
  getChangedSettings,
  getModuleSettingsForCategory,
  updateSavedSettingsForKeys,
} from "./companySystemSettings.config";
import {
  ChangedSetting,
  CompanySystemSettingsFormData,
  ModuleSettingItem,
  ModuleSettingKey,
  SystemSettingsCategory,
} from "./companySystemSettings.types";

type UseCompanySystemSettingsResult = {
  activeCategory: SystemSettingsCategory;
  setActiveCategory: React.Dispatch<
    React.SetStateAction<SystemSettingsCategory>
  >;
  formData: CompanySystemSettingsFormData;
  setFormData: React.Dispatch<
    React.SetStateAction<CompanySystemSettingsFormData>
  >;
  madeChanges: boolean;
  categoryOptions: SegmentOption<SystemSettingsCategory>[];
  moduleSettings: ModuleSettingItem[];
  activeCategoryHasSettings: boolean;
  activeCategoryHasChanges: boolean;
  isLoading: boolean;
  isReady: boolean;
  isSaving: boolean;
  loadFailed: boolean;
  handleUpdate: () => void;
  retryLoad: () => void;
  setModuleToggle: (key: ModuleSettingKey) => (value: boolean) => void;
};

export const useCompanySystemSettings = (): UseCompanySystemSettingsResult => {
  const { t } = useTranslation();
  const [companyState, , companyDispatch] = useCompanyStore();
  const companyId = companyState.company?.id;
  const [, , alertDispatch] = useAlertStore();

  const [activeCategory, setActiveCategory] =
    useState<SystemSettingsCategory>("gigs");
  const [formData, setFormData] =
    useState<CompanySystemSettingsFormData>(DEFAULT_FORM);
  const [hasLoadedInitialSettings, setHasLoadedInitialSettings] =
    useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const loadRequestIdRef = useRef(0);
  const saveRequestIdRef = useRef(0);
  const savedSettingsRef = useRef<CompanySystemSettingsFormData>(DEFAULT_FORM);
  const translationRef = useRef(t);

  useEffect(() => {
    translationRef.current = t;
  }, [t]);

  const showAlert = useCallback(
    (
      status: "loadError" | "partialSuccess" | "success" | "updateError",
    ): void => {
      const translate = translationRef.current;

      alertDispatch({
        type: "SHOW_ALERT",
        payload:
          status === "success"
            ? {
                icon: "checkmark",
                title: translate("Alerts.Updated"),
                message: translate("System.CompanySystemSettingsUpdated"),
              }
            : {
                icon: "alert",
                title: translate("Alerts.OhNo"),
                message:
                  status === "loadError"
                    ? translate("System.CompanySystemSettingsNotLoaded")
                    : status === "partialSuccess"
                      ? translate(
                          "System.CompanySystemSettingsPartiallyUpdated",
                        )
                      : translate("System.CompanySystemSettingsNotUpdated"),
              },
      });
    },
    [alertDispatch],
  );

  const categoryOptions = useMemo(() => getCategoryOptions(t), [t]);

  const loadSettings = useCallback((): void => {
    if (!companyId) return;

    const requestId = loadRequestIdRef.current + 1;
    loadRequestIdRef.current = requestId;
    setHasLoadedInitialSettings(false);
    setIsLoading(true);
    setLoadFailed(false);

    Api()
      .company.getCompanySettings(companyId)
      .then((response) => {
        if (loadRequestIdRef.current !== requestId) return;

        const initialFormData = buildInitialFormData(response.data);
        savedSettingsRef.current = initialFormData;
        setFormData(initialFormData);
        setHasLoadedInitialSettings(true);
        setLoadFailed(false);
      })
      .catch(() => {
        if (loadRequestIdRef.current !== requestId) return;

        setLoadFailed(true);
        showAlert("loadError");
      })
      .finally(() => {
        if (loadRequestIdRef.current !== requestId) return;
        setIsLoading(false);
      });
  }, [companyId, showAlert]);

  useEffect(() => {
    if (!companyId) return;
    loadSettings();
  }, [companyId, loadSettings]);

  useEffect(() => {
    saveRequestIdRef.current += 1;
    setIsSaving(false);
  }, [companyId]);

  useEffect(() => {
    return () => {
      loadRequestIdRef.current += 1;
      saveRequestIdRef.current += 1;
    };
  }, []);

  const diffPlan = useMemo(() => buildDiffPlan(), []);

  const changedSettings = useMemo<ChangedSetting[]>(
    () => getChangedSettings(formData, savedSettingsRef.current, diffPlan),
    [diffPlan, formData],
  );

  const madeChanges = changedSettings.length > 0;

  const changedSettingsForActiveCategory = useMemo(
    () =>
      changedSettings.filter((setting) =>
        CATEGORY_SETTING_KEYS[activeCategory].includes(setting.key),
      ),
    [activeCategory, changedSettings],
  );

  const handleUpdate = useCallback(() => {
    if (
      !companyId ||
      !hasLoadedInitialSettings ||
      isSaving ||
      changedSettingsForActiveCategory.length === 0
    ) {
      return;
    }

    const requestId = saveRequestIdRef.current + 1;
    saveRequestIdRef.current = requestId;
    setIsSaving(true);

    Promise.allSettled(
      changedSettingsForActiveCategory.map((setting) =>
        Api()
          .company.systemSettings.updateCompanySetting(
            companyId,
            setting.key,
            setting.value,
          )
          .then(() => setting),
      ),
    )
      .then((results) => {
        if (saveRequestIdRef.current !== requestId) return;

        const successfulSettings = results.flatMap((result) =>
          result.status === "fulfilled" ? [result.value] : [],
        );

        successfulSettings.forEach((setting) => {
          companyDispatch({
            type: "UPDATE_COMPANY_SETTINGS",
            payload: { key: setting.key, value: setting.value },
          });
        });

        if (successfulSettings.length > 0) {
          savedSettingsRef.current = updateSavedSettingsForKeys(
            successfulSettings.map((setting) => setting.key),
            formData,
            savedSettingsRef.current,
          );
          setFormData((previous) => ({ ...previous }));
        }

        if (
          successfulSettings.length === changedSettingsForActiveCategory.length
        ) {
          showAlert("success");
          return;
        }

        if (successfulSettings.length > 0) {
          showAlert("partialSuccess");
          return;
        }

        showAlert("updateError");
      })
      .finally(() => {
        if (saveRequestIdRef.current !== requestId) return;
        setIsSaving(false);
      });
  }, [
    changedSettingsForActiveCategory,
    companyDispatch,
    companyId,
    formData,
    hasLoadedInitialSettings,
    isSaving,
    showAlert,
  ]);

  const isReady = hasLoadedInitialSettings && !loadFailed;

  const retryLoad = useCallback((): void => {
    if (!companyId || isLoading) {
      return;
    }

    loadSettings();
  }, [companyId, isLoading, loadSettings]);

  const activeCategoryHasChanges = changedSettingsForActiveCategory.length > 0;

  const activeCategoryHasSettings = useMemo(
    () => getActiveCategoryHasSettings(activeCategory),
    [activeCategory],
  );

  const moduleSettings = useMemo(
    () => getModuleSettingsForCategory(activeCategory, t),
    [activeCategory, t],
  );

  const setModuleToggle =
    (key: ModuleSettingKey) =>
    (value: boolean): void => {
      setFormData((previous) => ({ ...previous, [key]: value }));
    };

  return {
    activeCategory,
    setActiveCategory,
    formData,
    setFormData,
    madeChanges,
    categoryOptions,
    moduleSettings,
    activeCategoryHasSettings,
    activeCategoryHasChanges,
    isLoading,
    isReady,
    isSaving,
    loadFailed,
    handleUpdate,
    retryLoad,
    setModuleToggle,
  };
};
